import styled from "@emotion/styled";
import { Global, Theme } from "@emotion/react";
import { H1, H2, Text, Link, SiteContainer } from "atoms";
import { Head } from "features/misc";

function TermsOfService() {
  return (
    <Main>
      <Head title="Terms of Service" />

      <Global styles={global} />
      <SiteContainer>
        <Header>
          <H1>The HUG Terms of Service</H1>
          <SText>Effective Date: July 13, 2022</SText>
        </Header>

        <Section>
          <Text>
            Thank you for visiting The HUG&apos;s (&#8220;
            <strong>The HUG</strong>,&#8221; &#8220;
            <strong>us</strong>,&#8221; &#8220;<strong>we</strong>,&#8221; or
            &#8220;
            <strong>our</strong>&#8221;) website (thehug.xyz) or other online
            services (the &#8220;<strong>Service</strong>&#8221;). These Terms
            of Use (the &#8220;
            <strong>Terms of Use</strong>&#8221; or the &#8220;
            <strong>Terms</strong>&#8221;) apply whenever you use the Service.
            In some instances, both these Terms and separate terms setting forth
            additional conditions may apply to specific Services offered
            (&#8220;
            <strong>Additional Terms</strong>&#8221;). Generally, to the extent
            there is a conflict between these Terms and any Additional Terms,
            these Terms will control unless stated otherwise. By accessing and
            using the Service in any manner, you acknowledge and agree to be
            bound by the version of these Terms in place at the time of your
            use.
          </Text>
          <Text>
            <strong>
              If you do not agree to these Terms, any Additional Terms, or our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>, please do not
              use our Service. Any use of our Service that is inconsistent with
              these Terms is deemed unauthorized access.
            </strong>
          </Text>
        </Section>

        <Section>
          <H2>Eligibility To Use Our Service</H2>
          <Text>
            You must be at least 16 years of age to access or use our Service or
            to create an account with us. You represent and warrant that (1) you
            are not a citizen of or located in a country or region that is
            targeted for comprehensive trade sanctions by the US Government, (2)
            you will not access or use our Service from such a country or
            region, and (3) you are not designated on the US Treasury&apos;s
            Specially Designated Nationals or Blocked Persons List or otherwise
            a person who is barred from doing business with US persons or from
            receiving exports of goods or Service from the US.
          </Text>
          <Text>
            If you register with us or create an account with us, you are solely
            responsible for the security and confidentiality of your access
            credentials, for restricting access to your device, and for all
            activity under your account. You must immediately notify us of any
            unauthorized use of your account, crypto wallet, or username, or any
            other breach of security, but you will remain responsible for any
            unauthorized use thereafter. Your account may be terminated if you
            or anyone else uses it in violation of these Terms. The HUG may
            deactivate accounts that are inactive and reject any application for
            a The HUG account for any reason.
          </Text>
        </Section>

        <Section>
          <H2>Limited Right To Use</H2>
          <Text>
            In exchange for your agreement to these Terms, The HUG grants you a
            limited, non-exclusive, personal, revocable right and license to
            access and use the functionality of our Service. The right to access
            and use the Service is licensed to you and is not being sold to you.
            You have no rights in the Service other than to use it in accordance
            with these Terms. We reserve the right to terminate your access to
            our Service at any time and for any or no reason. You may not copy,
            reproduce, print, distribute, transmit, display, perform, publish,
            license, modify, translate, adapt, create derivative works from,
            reverse engineer, decompile, disassemble, transfer, sell or
            otherwise exploit the Service, or any content, products or services
            obtained through the Service. No links may be established to any
            part of the Service and no information on the Service may be framed
            without our prior written approval.
          </Text>
          <Text>
            You may not use the Service in any manner that could disable,
            overburden, or impair any The HUG server, or interfere with any
            other person&apos;s use and enjoyment of the Service, other computer
            systems or networks connected to any The HUG server or to the
            Service, through hacking, password mining or any other means. You
            may use our Service only as permitted by law, including local,
            state, federal or international laws or regulations. We may suspend
            or stop providing our Service to you at any time and for any reason,
            including if you do not comply with our terms or policies, if we are
            investigating suspected misconduct or due to technical issues, and
            The HUG shall have no liability for such occurrences.
          </Text>
          <Text>
            We reserve all rights that are not expressly granted to you under
            these Terms.
          </Text>
        </Section>

        <Section>
          <H2>Intellectual Property Rights</H2>
          <Text>
            You agree and acknowledge that The HUG (and/or our licensors,
            vendors, agents, or content providers) owns all rights, title and
            interest in the Service, as well as all text, content, graphics,
            interfaces, code and materials, the look and feel, selection and
            arrangement, design and organization of the Service, and the
            compilation of the content, code, data and materials on the Service,
            including all intellectual property and proprietary rights. The
            Service is protected by copyright, trademark, trade dress, patent
            and/or other intellectual property and unfair competition laws of
            the United States and other countries. Any unauthorized use of
            material on the Service may violate such laws.
          </Text>
          <Text>
            The trademarks, logos and service marks used and displayed on the
            Service (collectively, the &#8220;<strong>Marks</strong>&#8221;) are
            registered and unregistered trademarks of, and owned by, The HUG or
            other owners that have granted The HUG the right and license to use
            such Marks. For clarity, there may be instances where trademark
            symbols are omitted for creative reasons, which does not impact the
            validity or enforceability of these Marks. You may not modify,
            publish, transmit, participate in the transfer or sale of, create
            derivative works from, or in any way exploit the Marks or any of the
            content of our Service, in whole or in part without our express,
            written permission. No copying, redistribution, retransmission,
            publication or commercial exploitation of the Marks or other
            material without the express written permission of The HUG or the
            copyright owner is permitted.
          </Text>
        </Section>

        <Section>
          <H2>Content You Submit</H2>

          <Text>
            You may create, post, upload, or link to content while using the
            Service (&#8220;<strong>User-Generated Content</strong>&#8221;). You
            are solely responsible for your User-Generated Content and for any
            harm resulting from it, regardless of the form of that Content. We
            are not responsible for any public display or misuse of your
            User-Generated Content.
          </Text>
          <Text>
            You represent and warrant that you: (i) have the right to transmit,
            distribute, replicate, and post any User-Generated Content you
            submit, (ii) you are the copyright owner or have the copyright
            owner&apos;s permission required to grant the rights to the
            User-Generated Content described in these Terms, (iii) you hold the
            rights necessary to grant the licenses described herein, (iv) you
            have obtained the express consent of each person, if any, depicted
            in your User-Generated Content, and (v) your User-Generated Content,
            and our use of that User-Generated Content as permitted under these
            Terms, do not and will not violate, misappropriate, or infringe any
            intellectual property rights, publicity rights or other proprietary
            rights of any third party.
          </Text>
          <Text>
            We have the right to refuse or remove any User-Generated Content
            that, in our sole discretion, violates any laws, our Privacy Policy
            or these Terms.
          </Text>
          <Text>
            You retain ownership of and responsibility for your User-Generated
            Content, including liability for any harm it may cause. However, we
            need the legal right to do things like host your content, publish
            it, and share it. By submitting User-Generated Content to us, you
            grant us and our legal successors the right to store, archive,
            modify, parse, and display your content, and to make copies of it,
            as necessary to provide the Service, including improving the Service
            over time. This license includes the right to do things like copy
            your User-Generated Content to our database and make backups; show
            it to you and other users; parse it into a search index or otherwise
            analyze it on our servers; share it with other users; and perform it
            (for example, if your User-Generated Content includes sound or
            video). By choosing to submit your User-Generated Content, you agree
            to waive any rights of integrity or attribution associated with your
            User-Generated Content, and agree to allow us to use your
            User-Generated Content without any reference or attribution to you.
          </Text>
          <Text>
            Additionally, if your User-Generated Content is set to be publicly
            viewable, you grant to other The HUG users a non-exclusive,
            personal, worldwide license to use, display, reproduce, and perform
            your User-Generated Content on the Service.
          </Text>
        </Section>

        <Section>
          <H2>Procedure For Alleging Copyright Infringement</H2>
          <Text>
            It is our policy to respond expeditiously when we receive a clear
            and complete notice of alleged copyright infringement that complies
            with the Digital Millennium Copyright Act (the &#8220;
            <strong>DMCA</strong>&#8221;). If we receive proper notification of
            claimed copyright infringement, we will remove or disable access to
            material claimed to be the subject of infringing activity in
            accordance with the DMCA, and in appropriate circumstances, disable
            and/or terminate the accounts of users who are repeat infringers. We
            will also promptly notify the alleged infringer that content has
            been removed or access disabled.
          </Text>
          <Text>
            We suggest that you consult your legal advisor before filing a DMCA
            notice or counter-notice. You may be liable for damages (including
            costs and attorneys&apos; fees) if you are engaged in copyright
            infringement or make a false claim of copyright infringement.
          </Text>
          <Text>
            <strong>How to Report Copyright Infringement.</strong> If you are a
            copyright owner who believes in good faith that materials hosted by
            us, including User-Generated Content, infringe your copyright,
            please provide the information requested below (including paragraph
            numbers) in the order requested:
          </Text>

          <ol>
            <li>
              A legend or subject line that says: &#8220;DMCA
              Notification;&#8221;
            </li>
            <li>
              A clear identification of the copyrighted work you claim was
              infringed;
            </li>
            <li>
              A clear identification of the material you claim is infringing the
              copyrighted work, and information that will allow us to locate
              that material on our Service, such as a link to the infringing
              material;
            </li>
            <li>
              Explain what would need to be done to remedy the alleged
              infringement;
            </li>
            <li>
              Your contact information so that we can reply to your complaint,
              preferably including an email address and telephone number;
            </li>
            <li>
              Contact information for the alleged infringer (if you have it);
              <br />
              <br />
              <i>Include the following statements:</i>
              <br />
              <br />
            </li>
            <li>
              &#8220;I have a good faith belief that the material that is
              claimed as copyright infringement is not authorized by the
              copyright owner, its agent, or the law.&#8221;
            </li>
            <li>
              &#8220;I swear, under penalty of perjury, that the information in
              the notification is accurate and I am the copyright owner, or am
              authorized to act on behalf of the owner, of an exclusive right
              that is allegedly infringed.&#8221;
            </li>
            <li>Include your physical or electronic signature.</li>
          </ol>
          <Text>
            The notice must be signed. Notices of claims of copyright
            infringement on our Service should be emailed or mailed, at your
            choice, to our DMCA Agent whose contact information is provided
            below.
          </Text>
          <Text>
            <strong>How to Submit a Counter-Notice.</strong> If you believe that
            your content that was removed from the Service is not infringing, or
            that you have authorization to post and use the material from the
            copyright owner, the copyright owner&apos;s agent or pursuant to the
            law, you may send our DMCA Agent a counter-notice containing the
            following information:
          </Text>
          <ol>
            <li>
              A legend or subject line that says: &#8220;DMCA-Counter
              Notification;&#8221;
            </li>
            <li>
              Clear identification of the content that has been removed and the
              location at which the content appeared before it was removed;
            </li>
            <li>
              A statement that you have a good faith belief that the content was
              removed or disabled as a result of mistake or a misidentification
              of the content. You may also explain why you believe this;
            </li>
            <li>
              Your contact information, preferably including an email address
              and telephone number;
            </li>
            <li>
              A statement that you consent to the jurisdiction of the Federal
              District Court for the Southern District of New York and a
              statement that you will accept service of process from the person
              who provided notification of the alleged infringement;
            </li>
            <li>Your physical or electronic signature.</li>
          </ol>
          <Text>
            If our DMCA Agent receives a counter-notice, we may send a copy of
            the counter-notice to the original complaining party informing that
            person that The HUG may replace the removed content in ten (10)
            business days. Unless the copyright owner files an action seeking a
            court order against the content provider or user, the removed
            Content may be replaced after receipt of the counter-notice, at The
            HUG&apos;s sole discretion.
          </Text>
          <Text>
            <strong>DMCA Agent.</strong> The contact information for our DMCA
            Agent is:
            <br />
            <br />
            Assemble Stream DMCA Agent
            <br />
            Assemble Stream Inc.
            <br />
            1178 Broadway
            <br />
            3rd Floor #1312
            <br />
            New York, NY 10001
            <br />
            Phone: (201) 834-6102
            <br />
            legal@assemblestream.com
            <br />
          </Text>
        </Section>

        <Section>
          <H2>Privacy</H2>
          <Text>
            Your privacy is important to us. Please review our{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>, which is
            incorporated into these Terms by reference.
          </Text>
        </Section>

        <Section>
          <H2>Availability Of The Service</H2>
          <Text>
            While we use commercially reasonable efforts to keep our Service
            accessible, it may be unavailable from time to time for indefinite
            amounts of time and for any reason including, without limitation,
            routine maintenance. The HUG is not responsible for any
            unavailability of the Service, for any coverage or connectivity
            issues you may experience, or any charges, data rates, or other fees
            incurred for cellular service, internet connection, etc.
          </Text>
        </Section>

        <Section>
          <H2>Accuracy And Errors</H2>
          <Text>
            Although we strive for accuracy, we do not warrant that the Service
            or any content hosted on it is complete, accurate, reliable,
            current, or error-free. Please use your best judgment when using our
            Service. The HUG is not responsible for your interpretation of or
            reliance on any information or content found on the Service, and
            makes no representations about the accuracy, reliability,
            completeness, or timeliness of the Service. We reserve the right to
            correct any errors, inaccuracies or omissions and to change or
            update information on our Service at any time without prior notice.
          </Text>
        </Section>

        <Section>
          <H2>Additional Terms Govern NFT Minting Process</H2>
          <Text>
            A separate set of terms (the &#8220;<strong>HUGmint Terms</strong>
            &#8221;) govern our proprietary self-serve software application that
            allows users to create, custody, and transfer nonfungible tokens
            (&#8220;
            <strong>NFTs</strong>&#8221;) on the Ethereum Network. To the extent
            there is a conflict between these Terms of Use and the HUGmint
            Terms, the HUGmint Terms shall control where applicable.
          </Text>
        </Section>

        <EmphasizedSection>
          <H2>Disclaimer Of Representations And Warranties</H2>
          <Text>
            To the maximum extent permitted by applicable law, your access to
            and use of the service is at your sole risk, and the service is
            provided on an &#8220;as is,&#8221; &#8220;as available,&#8221; and
            &#8220;with all faults&#8221; basis. The hug and its direct and
            indirect parents, subsidiaries, affiliates, and each of their
            respective employees, directors, members, managers, shareholders,
            agents, vendors, licensors, licensees, contractors, customers,
            successors, and assigns (collectively, the &#8220;
            <strong>the hug parties</strong>&#8221;) hereby disclaim and make no
            representations, warranties, endorsements, or promises, express or
            implied, in connection with, or otherwise directly or indirectly
            related to the service, except as set forth below.
          </Text>
          <Text>
            Except for any specific warranties provided herein or as required by
            applicable law, the the hug parties hereby further disclaim all
            warranties, express or implied, including the warranties of
            merchantability, fitness for a particular purpose, non-infringement,
            or misappropriation of intellectual property rights of third
            parties, title, custom, trade, quiet enjoyment, system integration,
            and freedom from computer viruses.
          </Text>
        </EmphasizedSection>

        <EmphasizedSection>
          <H2>Limitation Of Our Liability</H2>
          <Text>
            To the maximum extent permitted by applicable law, in no event shall
            the the hug parties or any of their employees, agents, partners,
            service providers, content providers or licensors be liable for any
            indirect, incidental, special, consequential or punitive damages
            however caused, whether for breach of warranty, breach of contract,
            product liability, negligence, strict liability or otherwise,
            arising out of the use of or inability to use the service, arising
            out of the content and other information contained therein or in
            these terms of use, or your access to, use of, or inability to
            access or use the service, or any content obtained from the service,
            even if the the hug parties have been advised of the possibility of
            such damages.
          </Text>
          <Text>
            In no event will the the hug parties&apos; total liability to you
            exceed, in the aggregate for all possible damages, losses, and
            causes of action in connection with your access to and use of the
            service and your rights under these terms, an amount that is the
            lesser of two hundred and fifty dollars (usd $250.00) or the amount
            that you have paid the hug in the last year for access to the
            service.
          </Text>
          <Text>
            You acknowledge that the hug would not provide access to the service
            if not for the foregoing limitations of liability, and that these
            limitations of liability are essential elements of the terms. These
            limitations of liability reflect the allocation of risk between the
            parties. the limitations specified in this section will survive and
            apply even if any limited remedy specified in these terms or any
            applicable additional terms is found to have failed of its essential
            purpose.
          </Text>
          <Text>
            Because some jurisdictions do not allow a limitation or exclusion of
            liability in all circumstances, in those jurisdictions, the the hug
            parties&apos; liability shall be limited to the extent permitted by
            law.
          </Text>
        </EmphasizedSection>

        <Section>
          <H2>Indemnification</H2>
          <Text>
            Except where specifically prohibited by applicable law, you agree to
            defend (if requested by The HUG), indemnify, and hold the The HUG
            Parties harmless from and against any and all claims, damages,
            losses, costs, investigations, liabilities, judgments, fines,
            penalties, settlements, interest, and expenses (including
            attorneys&apos; fees) that directly or indirectly arise from or are
            related to any claim, suit, action, demand, or proceeding made or
            brought against any The HUG Party, or on account of the
            investigation, defense, or settlement thereof, arising out of or in
            connection with: (i) your User-Generated Content; (ii) your use of
            the Service and your activities in connection with the Service;
            (iii) your breach or alleged breach of these Terms; (iv) your
            violation or alleged violation of any laws, rules, regulations,
            codes, statutes, ordinances, or orders of any governmental or
            quasi-governmental authorities in connection with your use of the
            Service or your activities in connection with the Service; (v)
            information or material transmitted through your device, even if not
            submitted by you, that infringes, violates, or misappropriates any
            copyright, trademark, trade secret, trade dress, patent, publicity,
            privacy, or other right of any person or entity; and (vi) any
            misrepresentation made by you (all of the foregoing, &#8220;
            <strong>Claims and Losses</strong>&#8221;). The The HUG Parties
            reserve the right to assume the exclusive defense and control of any
            Claims and Losses; however, you will cooperate as fully as required
            by the The HUG Parties in the defense of any Claims and Losses. The
            The HUG Parties retain the exclusive right to settle, compromise,
            and determine whether to pay any and all Claims and Losses. You will
            not settle any Claims and Losses without, in each instance, the
            prior written consent of an officer of the impacted The HUG Party.
            This section is not intended to limit any cause of action against us
            that you may have but that is not waivable under applicable law.
          </Text>
        </Section>

        <Section>
          <H2>Dispute Resolution Terms</H2>
          <EmphasizedSection as="p">
            Please read this section carefully. It affects your legal rights,
            including your right to file a lawsuit in court and to have your
            claim heard by a jury.
          </EmphasizedSection>
          <Text>
            <strong>Informal Dispute Resolution.</strong> If you have a dispute
            with us, please contact us and we will try to resolve it. Pursuant
            to these terms, you agree that you will make a good faith effort to
            informally resolve any disputes that may arise between you and us
            before resorting to arbitration, and agree to provide written notice
            to the email or mailing addresses listed below describing your claim
            so that we can attempt to resolve it with you. If we do not
            satisfactorily resolve your claim within thirty (30) calendar days
            of receiving your written notice, then you may provide a second
            written notice explaining your intent to pursue your claim in
            arbitration. You may not initiate arbitration without first
            following the informal dispute resolution procedure provided in this
            paragraph.
          </Text>
          <Text>
            <strong>Agreement to Arbitration.</strong> Any dispute, claim, or
            cause of action you may have against us that is not resolved
            informally through the process described in the preceding paragraph
            shall be settled by binding arbitration administered by the AAA in
            accordance with its Consumer Arbitration Rules or Commercial
            Arbitration Rules, as applicable depending on the amount in
            controversy. Payment of all filing, administration and arbitrator
            fees will be governed by the AAA&apos;s applicable rules, and we
            will reimburse those fees if your claims at issue seek less than
            $10,000. Likewise, we will not seek attorneys&apos; fees and costs
            in arbitration. Judgment on any award rendered by the arbitrator may
            be entered in any state or federal court of competent jurisdiction.
          </Text>
          <Text>
            <strong>Exclusions from Arbitration.</strong> Notwithstanding the
            above, you or we may choose to pursue a dispute in court and not by
            arbitration if the dispute qualifies for resolution in small claims
            court. We may also seek injunctive relief, damages, or other
            appropriate relief in state or federal court if the dispute involves
            an infringement or threatened infringement of our intellectual
            property rights. Any such dispute brought by you or us in court
            shall be filed in the state or federal courts located in New York
            County, NY, and you consent and agree to the personal jurisdiction
            and venue of these courts.
          </Text>
          <Text>
            <strong>Class Action Waiver.</strong> You agree that, to the maximum
            extent permitted by applicable law, any and all disputes, claims,
            and causes of action arising out of or connected with our Service,
            these Terms, or our Privacy Policy will be resolved on an individual
            basis, without resort to any form of class action or mass action.
            This provision shall not be construed as a waiver of your right to
            seek public injunctive relief as provided under California law to
            the extent such right may otherwise be available to you.
          </Text>
          <Text>
            <strong>Time Limitation.</strong> You agree to bring any and all
            claims you may have against The HUG within one (1) year from the
            date of accrual of a cause of action, and that actions brought after
            this date will be deemed time-barred.
          </Text>
        </Section>

        <Section>
          <H2>Governing Law</H2>
          <Text>
            Any disputes arising out of or related to these Terms and/or your
            use of our Service shall be governed by the laws of the United
            States and the State of New York, without regard to choice-of-law
            rules and without regard to conflicts-of-laws principles.
          </Text>
        </Section>

        <Section>
          <H2>General Provisions</H2>
          <Text>
            <strong>Service Not Intended for International Use.</strong> The HUG
            controls and operates the Service from the US, and makes no
            representation that the Service is appropriate or available for use
            beyond the US. If you use the Service from other locations, you are
            doing so on your own initiative, at your own risk, and are
            responsible for compliance with applicable local laws regarding your
            online conduct and acceptable content.
          </Text>
          <Text>
            <strong>No Waiver.</strong> No delay or failure by The HUG to
            enforce any provision in these Terms shall constitute a waiver of
            any of The HUG&apos;s rights. Neither the receipt of any funds by
            The HUG nor the reliance of any person on our actions shall be
            deemed to constitute a waiver of any part of these Terms. Only a
            specific, written waiver signed by an authorized representative of
            The HUG shall have any legal effect.
          </Text>
          <Text>
            <strong>Severability.</strong> If any provision of these Terms, or
            any applicable Additional Terms, is for any reason deemed invalid,
            unlawful, void, or unenforceable by a court or arbitrator of
            competent jurisdiction, then that provision will be deemed severable
            from these Terms, and the invalidity of the provision will not
            affect the validity or enforceability of the remainder of these
            Terms. To the extent permitted by applicable law, you agree to waive
            and will waive, any applicable statutory and common law that may
            permit a contract to be construed against its drafter.
          </Text>
          <Text>
            <strong>Modification.</strong> We reserve the right to make changes
            to these Terms at any time, and will post an updated version on our
            Service. Please check back from time to time to ensure you are aware
            of any updates or changes to these Terms. Any new Terms will be
            effective as of the time that we post them. In the event any notice
            to you of new, revised, or additional terms is determined by a
            tribunal to be insufficient, the prior version shall continue in
            effect until sufficient notice to establish a new agreement occurs.
            You can reject any new, revised or additional terms by discontinuing
            use of the Service.
          </Text>
          <Text>
            <strong>
              Investigations; Cooperation with Law Enforcement; Termination;
              Survival.
            </strong>
            Except where specifically prohibited by applicable law, The HUG
            reserves the right, without limitation, to: (i) investigate any
            suspected breaches of the security of its Service and information
            technology or other systems or networks, (ii) investigate any
            suspected breaches of these Terms, (iii) involve and cooperate with
            law enforcement authorities in investigating any of the foregoing
            matters, (iv) pursue legal claims against violators of these Terms,
            and (v) discontinue the Service, in whole or in part, or, suspend or
            terminate your access to the Service, in whole or in part, including
            any user accounts or registrations, at any time, without notice, for
            any reason and without any obligation to you or any third party. Any
            suspension or termination will not affect your obligations to The
            HUG under these Terms. Upon suspension or termination of your access
            to the Service, or upon notice from The HUG, all rights granted to
            you under these Terms will cease immediately, and you agree that you
            will immediately discontinue use of the Service. The provisions of
            these Terms, which by their nature should survive your suspension or
            termination will survive, including the rights and licenses you
            grant to The HUG, as well as the indemnities, releases, disclaimers,
            and limitations on liability and the provisions regarding dispute
            resolution.
          </Text>
          <Text>
            <strong>Assignment.</strong> The HUG may assign its rights and
            obligations under these Terms, in whole or in part, to any party at
            any time without any notice. These Terms may not be assigned by you,
            and you may not delegate your duties under them, without the prior
            written consent of an officer of The HUG.
          </Text>
        </Section>

        <Section>
          <H2>How To Contact Us</H2>
          <Text>
            If you have any comments or questions about these Terms,
            communications can be submitted to our postal address, via email to
            legal@assemblestream.com or by calling (201) 834-6102.
          </Text>
          <SText size="sm">
            <strong>Assemble Stream Inc.</strong>
            <address>
              1178 Broadway
              <br />
              3rd Floor #1312
              <br />
              New York, NY 10001
            </address>
          </SText>
        </Section>
      </SiteContainer>
    </Main>
  );
}

const Main = styled.main({}, ({ theme }) => ({
  background: theme.gradients.radial2,
}));

const Header = styled.header({
  padding: "0 0 4rem",
  position: "relative",
  textAlign: "center",
});

const Section = styled.section({
  padding: "2rem 0",
});

const EmphasizedSection = styled(Section)(({ theme }) => ({
  border: `2px solid ${theme.colors.red}`,
  borderRadius: theme.borderRadius.md,
  margin: "2rem 0",
  padding: "1rem",
  "@media (min-width: 600px)": {
    padding: "1.5rem",
  },
}));

const global = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.yellow,
  },
});

const SText = styled(Text)({
  address: {
    fontStyle: "normal",
  },
});

export { TermsOfService };
